import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTramaClient } from '../context.js';

interface CheckoutResponse {
  checkoutUrl: string;
}

export interface UseCheckoutReturn {
  createCheckout: (cartId: string) => Promise<string>;
  redirectToCheckout: (cartId: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

export function useCheckout(): UseCheckoutReturn {
  const client = useTramaClient();

  const mutation = useMutation({
    mutationFn: (cartId: string) =>
      client.request<CheckoutResponse>('/api/v1/checkout', {
        method: 'POST',
        body: { cartId },
      }),
  });

  const createCheckout = useCallback(
    async (cartId: string) => {
      const result = await mutation.mutateAsync(cartId);
      return result.checkoutUrl;
    },
    [mutation],
  );

  const redirectToCheckout = useCallback(
    async (cartId: string) => {
      const url = await createCheckout(cartId);
      if (typeof window !== 'undefined') window.location.href = url;
    },
    [createCheckout],
  );

  return {
    createCheckout,
    redirectToCheckout,
    isLoading: mutation.isPending,
    error: mutation.error as Error | null,
  };
}
